/**
 * Gestión de los **subdiarios de CONCAR**.
 *
 * Es el modal `#modal_subdiarios` de `views/menu/reportes/exportar_concar.php` (con
 * su parte de `src/reports/concar.js`). Sirve para ordenar los subdiarios: el
 * primero se usa en el Libro 1 del Excel, el segundo en el Libro 2 y así (cuando un
 * período pasa de 9 999 comprobantes, CONCAR necesita un libro y un subdiario por
 * cada bloque).
 *
 * Reglas heredadas y comprobadas:
 * - El **orden** lo pone la pantalla (subir y bajar filas) y es el que se guarda.
 * - Al guardar, la API reemplaza la lista entera (el original hacía `truncate` +
 *   `insert`), así que lo que se ve en el diálogo es lo que queda.
 * - Un subdiario con `activo = 0` queda en la lista pero no se usa.
 *
 * Diferencias anotadas: el código se limita a los **dos dígitos** de CONCAR y no se
 * admiten repetidos (el original aceptaba cualquier texto y dos veces el mismo
 * código, y el Excel salía con subdiarios repetidos).
 */

import { ArrowDown, ArrowUp, Plus, Save, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useQueryClient } from '@tanstack/react-query';
import { concarApi, concarKeys } from '../exportar-concar.api';
import type { ListaSubdiarios, SubdiarioConcar } from '../exportar-concar.types';

/** Una fila del diálogo, tal como la edita el usuario. */
interface Fila {
  id?: number;
  codigo: string;
  nombre: string;
  activo: boolean;
}

const CONTROL =
  'h-9 w-full rounded-control border border-ink-200 bg-white px-2.5 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

export function SubdiariosDialog(props: {
  open: boolean;
  onClose: () => void;
  datos: ListaSubdiarios | undefined;
}) {
  // La lista del servidor es la que manda: el formulario se vuelve a montar
  // cuando cambia (al abrir el diálogo con datos nuevos o después de guardar),
  // así que su estado siempre sale de lo que hay en la base de datos.
  const clave = (props.datos?.subdiarios ?? [])
    .map((subdiario) => subdiario.id ?? subdiario.codigo_subdiario)
    .join('-');

  return <FormularioSubdiarios key={clave} {...props} />;
}

function FormularioSubdiarios({
  open,
  onClose,
  datos,
}: {
  open: boolean;
  onClose: () => void;
  datos: ListaSubdiarios | undefined;
}) {
  const cliente = useQueryClient();

  const [filas, setFilas] = useState<Fila[]>(() =>
    (datos?.subdiarios ?? []).map((subdiario) => ({
      id: subdiario.id,
      codigo: String(subdiario.codigo_subdiario ?? ''),
      nombre: String(subdiario.nombre ?? ''),
      activo: Number(subdiario.activo) === 1 || subdiario.activo === true,
    })),
  );
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  const mover = (indice: number, salto: number) => {
    setFilas((actuales) => {
      const destino = indice + salto;
      const fila = actuales[indice];

      if (!fila || destino < 0 || destino >= actuales.length) return actuales;

      const copia = [...actuales];

      copia.splice(indice, 1);
      copia.splice(destino, 0, fila);

      return copia;
    });
  };

  const quitar = async (indice: number) => {
    const fila = filas[indice];

    if (!fila) return;

    setError(null);

    // Si ya está guardado se borra también en el servidor, como el original.
    if (fila.id) {
      try {
        await concarApi.borrarSubdiario(fila.id);
        await cliente.invalidateQueries({ queryKey: concarKeys.subdiarios });
      } catch (fallo) {
        setError(fallo instanceof Error ? fallo.message : 'No se pudo eliminar el subdiario.');
        return;
      }
    }

    setFilas((actuales) => actuales.filter((_, posicion) => posicion !== indice));
  };

  const guardar = async () => {
    setError(null);
    setAviso(null);

    const codigos = filas.map((fila) => fila.codigo.trim());
    const repetido = codigos.find((codigo, indice) => codigo !== '' && codigos.indexOf(codigo) !== indice);

    if (filas.length === 0) {
      setError('Deja al menos un subdiario: es el que usa el Libro 1 del Excel.');
      return;
    }

    if (codigos.some((codigo) => !/^\d{1,2}$/.test(codigo))) {
      setError('Cada subdiario necesita un código de uno o dos dígitos (por ejemplo 02).');
      return;
    }

    if (repetido) {
      setError(`El subdiario ${repetido} está repetido: cada libro necesita uno distinto.`);
      return;
    }

    const cuerpo: SubdiarioConcar[] = filas.map((fila, indice) => ({
      codigo_subdiario: fila.codigo.trim().padStart(2, '0'),
      nombre: fila.nombre.trim() === '' ? `Subdiario ${fila.codigo.trim()}` : fila.nombre.trim(),
      orden: indice + 1,
      activo: fila.activo ? 1 : 0,
    }));

    setGuardando(true);

    try {
      const respuesta = await concarApi.guardarSubdiarios(cuerpo);

      await cliente.invalidateQueries({ queryKey: concarKeys.subdiarios });
      await cliente.invalidateQueries({ queryKey: concarKeys.todo });
      setAviso(respuesta.message ?? 'Subdiarios guardados.');
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No se pudieron guardar los subdiarios.');
    } finally {
      setGuardando(false);
    }
  };

  const activos = filas.filter((fila) => fila.activo).map((fila) => fila.codigo.padStart(2, '0'));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Gestionar subdiarios"
      description="El primer subdiario se usa en el Libro 1 del Excel, el segundo en el Libro 2 y así sucesivamente."
      size="lg"
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cerrar
          </Button>
          <Button
            leftIcon={<Save className="size-4" aria-hidden="true" />}
            loading={guardando}
            onClick={() => void guardar()}
          >
            Guardar cambios
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <p className="text-xs text-ink-500">
          Se usarán en orden:{' '}
          <strong className="text-ink-700">
            {activos.length > 0 ? activos.join(', ') : 'ninguno activo'}
          </strong>
          {datos?.porDefecto.length ? (
            <>
              . Si no queda ninguno activo, la API usa {datos.porDefecto.join(', ')}.
            </>
          ) : null}
        </p>

        {error ? (
          <Alert variant="error" title="Revisa la lista de subdiarios">
            {error}
          </Alert>
        ) : null}

        {aviso ? <Alert variant="success">{aviso}</Alert> : null}

        <div className="overflow-auto rounded-card border border-ink-100">
          <table className="w-full border-collapse text-sm">
            <thead className="bg-ink-50/70 text-left text-xs font-semibold tracking-wide text-ink-500 uppercase">
              <tr>
                <th className="px-2 py-2">Orden</th>
                <th className="px-2 py-2">Código</th>
                <th className="px-2 py-2">Nombre</th>
                <th className="px-2 py-2">Activo</th>
                <th className="px-2 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filas.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-2 py-3 text-center text-ink-500">
                    No hay subdiarios: agrega al menos uno.
                  </td>
                </tr>
              ) : (
                filas.map((fila, indice) => (
                  <tr key={`${fila.id ?? 'nuevo'}-${indice}`} className="border-t border-ink-100">
                    <td className="px-2 py-1.5 text-ink-500 tabular-nums">{indice + 1}</td>
                    <td className="px-2 py-1.5">
                      <input
                        className={`${CONTROL} w-16 text-center`}
                        value={fila.codigo}
                        maxLength={2}
                        aria-label={`Código del subdiario ${indice + 1}`}
                        onChange={(evento) =>
                          setFilas((actuales) =>
                            actuales.map((actual, posicion) =>
                              posicion === indice
                                ? { ...actual, codigo: evento.target.value.replace(/\D/g, '') }
                                : actual,
                            ),
                          )
                        }
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <Input
                        className={CONTROL}
                        containerClassName="w-full"
                        value={fila.nombre}
                        aria-label={`Nombre del subdiario ${indice + 1}`}
                        onChange={(evento) =>
                          setFilas((actuales) =>
                            actuales.map((actual, posicion) =>
                              posicion === indice ? { ...actual, nombre: evento.target.value } : actual,
                            ),
                          )
                        }
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <Checkbox
                        label=""
                        checked={fila.activo}
                        onChange={(evento) =>
                          setFilas((actuales) =>
                            actuales.map((actual, posicion) =>
                              posicion === indice
                                ? { ...actual, activo: evento.target.checked }
                                : actual,
                            ),
                          )
                        }
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Subir"
                          aria-label={`Subir el subdiario ${fila.codigo}`}
                          disabled={indice === 0}
                          onClick={() => mover(indice, -1)}
                        >
                          <ArrowUp className="size-4" aria-hidden="true" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Bajar"
                          aria-label={`Bajar el subdiario ${fila.codigo}`}
                          disabled={indice === filas.length - 1}
                          onClick={() => mover(indice, 1)}
                        >
                          <ArrowDown className="size-4" aria-hidden="true" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Eliminar"
                          aria-label={`Eliminar el subdiario ${fila.codigo}`}
                          onClick={() => void quitar(indice)}
                        >
                          <Trash2 className="size-4 text-rose-500" aria-hidden="true" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Button
          variant="outline"
          size="sm"
          leftIcon={<Plus className="size-4" aria-hidden="true" />}
          onClick={() =>
            setFilas((actuales) => [
              ...actuales,
              { codigo: '', nombre: '', activo: true },
            ])
          }
        >
          Agregar subdiario
        </Button>
      </div>
    </Dialog>
  );
}
